import React, { useState, useEffect } from 'react';
import {
  Grid,
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  LinearProgress,
  Chip,
  Avatar,
} from '@mui/material';
import {
  People as PeopleIcon,
  TrendingUp as TrendingUpIcon,
  AccessTime as AccessTimeIcon,
  AttachMoney as AttachMoneyIcon,
  Visibility as VisibilityIcon,
  LocalHospital as HospitalIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { analyticsAPI, visitAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import StatCard from '../components/StatCard';

const Dashboard = () => {
  const { user } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [todayVisits, setTodayVisits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const [analyticsRes, visitsRes] = await Promise.all([
        analyticsAPI.getDashboard(),
        visitAPI.getTodayVisits(),
      ]);

      setAnalytics(analyticsRes.data.data);
      setTodayVisits(visitsRes.data.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (seconds) => {
    if (!seconds) return 'N/A';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <Box>
      {/* Welcome Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: { xs: 'flex-start', md: 'center' },
          flexDirection: { xs: 'column', md: 'row' },
          gap: { xs: 2, md: 0 },
          mb: 3
        }}>
          <Box>
            <Typography 
              variant="h3" 
              fontWeight="700" 
              color="#3D3B54" 
              sx={{ 
                mb: 1,
                fontSize: { xs: '1.75rem', md: '2.25rem' }
              }}
            >
              Dashboard
            </Typography>
            <Typography variant="h6" color="#7B7A92" fontWeight="400">
              Welcome back, {user?.name || 'User'}! Here's your clinic overview.
            </Typography>
          </Box>
          <Box sx={{ textAlign: { xs: 'left', md: 'right' } }}>
            <Typography variant="h6" fontWeight="600" color="#3D3B54">
              {new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
            </Typography>
            <Typography variant="body2" color="#7B7A92">
              {new Date().toLocaleDateString('en-US', { weekday: 'long' })}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Key Metrics Section */}
      <Box sx={{ mb: { xs: 3, md: 4 } }}>
        <Typography variant="h5" fontWeight="700" sx={{ mb: { xs: 2, md: 3 } }}>
          Key Metrics
        </Typography>
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
          <StatCard
            label="Today's Visits"
            value={analytics?.todayVisits || 0}
            icon={<VisibilityIcon fontSize="small" />}
            color="primary"
            subtitle="Patients seen today"
          />
          <StatCard
            label="Total Patients"
            value={analytics?.totalPatients || 0}
            icon={<PeopleIcon fontSize="small" />}
            color="secondary"
            subtitle="Registered patients"
          />
          <StatCard
            label="Revenue Today"
            value={`₹${analytics?.revenueToday?.toLocaleString() || 0}`}
            icon={<AttachMoneyIcon fontSize="small" />}
            color="info"
            subtitle="Today's earnings"
          />
          <StatCard
            label="Completed"
            value={analytics?.visitsByStatus?.find(v => v._id === 'completed')?.count || 0}
            icon={<CheckCircleIcon fontSize="small" />}
            color="success"
            subtitle="Finished appointments"
          />
        </Box>
      </Box>

      {/* Clinic Status Section */}
      <Box sx={{ mb: { xs: 3, md: 4 } }}>
        <Typography variant="h5" fontWeight="700" sx={{ mb: { xs: 2, md: 3 } }}>
          Clinic Status
        </Typography>

        {analytics?.visitsByStatus?.length > 0 ? (
          <Grid container spacing={{ xs: 2, md: 2 }}>
            {analytics.visitsByStatus.map((status) => {
              const statusConfig = {
                'checked-in': { color: 'primary', label: 'Checked In' },
                'in-reception': { color: 'info', label: 'In Reception' },
                'waiting-optometrist': { color: 'secondary', label: 'Waiting Optometrist' },
                'in-optometrist': { color: 'secondary', label: 'With Optometrist' },
                'waiting-doctor': { color: 'warning', label: 'Waiting Doctor' },
                'in-doctor': { color: 'warning', label: 'With Doctor' },
                'waiting-opd': { color: 'warning', label: 'Waiting OPD' },
                'in-opd': { color: 'info', label: 'In OPD' },
                'completed': { color: 'success', label: 'Completed' },
              };
              const config = statusConfig[status._id] || { color: 'primary', label: status._id };

              return (
                <Grid item xs={12} sm={6} md={4} lg={3} key={status._id}>
                  <Card
                    variant="outlined"
                    sx={{
                      p: { xs: 2, md: 2.5 },
                      borderLeft: '3px solid',
                      borderLeftColor: `${config.color}.main`,
                      height: '100%',
                      boxShadow: 'none',
                    }}
                  >
                    <Typography variant="h3" fontWeight="700" sx={{ lineHeight: 1.2, fontSize: { xs: '1.5rem', md: '1.875rem' } }}>
                      {status.count}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" fontWeight={600}>
                      {config.label}
                    </Typography>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        ) : (
          <Card
            variant="outlined"
            sx={{
              p: { xs: 4, md: 6 },
              textAlign: 'center',
              boxShadow: 'none',
            }}
          >
            <ScheduleIcon sx={{ fontSize: { xs: 48, md: 64 }, color: '#CBD5E1', mb: 2 }} />
            <Typography color="text.secondary" variant="h6" fontWeight={600} sx={{ mb: 1 }}>No visits today</Typography>
            <Typography color="text.secondary" variant="body2">
              Patient appointments will appear here as they are scheduled
            </Typography>
          </Card>
        )}
      </Box>

      {/* Recent Activity Section */}
      <Box sx={{ mb: { xs: 3, md: 4 } }}>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          mb: { xs: 2, md: 3 },
          flexDirection: { xs: 'column', sm: 'row' },
          gap: { xs: 2, sm: 0 }
        }}>
          <Typography variant="h5" fontWeight="700">
            Recent Activity
          </Typography>
          <Chip
            label={`${todayVisits.length} visits today`}
            color="primary"
            variant="outlined"
            sx={{ fontWeight: 600 }}
          />
        </Box>

        {todayVisits.length > 0 ? (
          <Card variant="outlined" sx={{ overflow: 'hidden', boxShadow: 'none' }}>
            <Box sx={{ p: { xs: 1, md: 1.5 } }}>
              {todayVisits.slice(0, 8).map((visit, index) => {
                const statusConfig = {
                  'checked-in': { color: 'primary', label: 'Checked In' },
                  'in-reception': { color: 'info', label: 'In Reception' },
                  'in-optometrist': { color: 'secondary', label: 'With Optometrist' },
                  'in-doctor': { color: 'warning', label: 'With Doctor' },
                  'in-opd': { color: 'info', label: 'In OPD' },
                  'completed': { color: 'success', label: 'Completed' }
                };
                const config = statusConfig[visit.status] || statusConfig['checked-in'];

                return (
                  <Box
                    key={visit._id}
                    sx={{
                      p: { xs: 1.5, md: 2 },
                      borderBottom: index < todayVisits.length - 1 ? '1px solid #E9E9F2' : 'none',
                    }}
                  >
                    <Box sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      flexDirection: { xs: 'column', sm: 'row' },
                      gap: { xs: 1.5, sm: 0 }
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                        <Avatar sx={{
                          bgcolor: '#F4F4F9',
                          color: 'text.secondary',
                          mr: { xs: 2, md: 2.5 },
                          width: { xs: 36, md: 40 },
                          height: { xs: 36, md: 40 },
                          fontSize: '0.875rem',
                          fontWeight: 600,
                        }}>
                          {index + 1}
                        </Avatar>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="body1" fontWeight="600" sx={{ mb: 0.25 }}>
                            {visit.patient?.name}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                            <Typography variant="caption" color="text.secondary">
                              ID: {visit.patient?.patientId}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Visit: {visit.visitId}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Chip label={config.label} size="small" color={config.color} variant="outlined" sx={{ fontWeight: 600 }} />
                        <Typography variant="caption" color="text.secondary" sx={{ minWidth: 60, textAlign: 'right' }}>
                          {new Date(visit.visitDate).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Card>
        ) : (
          <Card
            variant="outlined"
            sx={{
              p: { xs: 4, md: 6 },
              textAlign: 'center',
              boxShadow: 'none',
            }}
          >
            <VisibilityIcon sx={{ fontSize: { xs: 60, md: 80 }, color: '#E9E9F2', mb: 3 }} />
            <Typography color="#7B7A92" variant="h5" fontWeight={600} sx={{ mb: 1 }}>No recent activity</Typography>
            <Typography color="#9B9AB0" variant="body1">
              Patient visits and activities will appear here
            </Typography>
          </Card>
        )}
      </Box>
    </Box>
  );
};

export default Dashboard;
